import { LocalizedTablePagination } from "@/components/common/LocalizedTablePagination";
import { RecordFields, RecordList } from "@/components/common/RecordList";
import { RecordSortControl } from "@/components/common/RecordSortControl";
import {
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Edit as EditIcon,
  VpnKey as ApiKeysIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  AdminDatePicker,
  AdminDialog,
  AdminEmptyState,
  AdminTableActions,
  AdminTableBodyState,
  AdminTablePanel,
  AuditActor,
  PageShell,
} from "@/components/common";
import { LoadingRing } from "@/components/common/LoadingRing";
import MuiAutocompleteMultiSelect from "@/components/common/MuiAutocompleteMultiSelect";
import { SortableTableCell } from "@/components/common/SortableTableCell";
import { useTableSorting } from "@/hooks/useTableSorting";
import { ApiKey, ApiKeyService } from "@/services/features/ApiKeyService";
import { Customer, CustomerService } from "@/services/features/CustomerService";
import { getAuditActorName } from "@/utils/auditActor";
import { getRoleDisplayName, UserRole } from "@/utils/roleManager";
import { formatStoreDateTimeWithTimezone, getBrowserTimezone } from "@/utils/storeTime";

const DetailRow: React.FC<{
  label: string;
  value: React.ReactNode;
  monospace?: boolean;
}> = ({ label, value, monospace = false }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "1fr", sm: "140px 1fr" },
      gap: 0.5,
    }}
  >
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontFamily: monospace ? "monospace" : undefined }}>
      {value || "-"}
    </Typography>
  </Box>
);

const parseDateOnly = (value?: string | null): Date | null => {
  if (!value) return null;

  const [year, month, day] = value.slice(0, 10).split("-").map(Number);
  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day);
};

const toDateOnlyKey = (date: Date): string =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");

const toDateOnlyIsoString = (date: Date | null): string | null =>
  date ? `${toDateOnlyKey(date)}T00:00:00.000Z` : null;

const ApiKeys: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createdApiKey, setCreatedApiKey] = useState<ApiKey | null>(null);
  const [editingApiKey, setEditingApiKey] = useState<ApiKey | null>(null);
  const [editValidUntil, setEditValidUntil] = useState("");
  const [editActive, setEditActive] = useState(false);
  const [isSavingApiKey, setIsSavingApiKey] = useState(false);
  const [formData, setFormData] = useState({
    customerId: "",
    name: "",
    role: UserRole.STORE_ADMIN,
    validUntil: "",
  });
  const [createDialogError, setCreateDialogError] = useState<string | null>(null);
  const [editDialogError, setEditDialogError] = useState<string | null>(null);
  const [createFieldErrors, setCreateFieldErrors] = useState({ customerId: false, name: false });

  const apiKeyService = useMemo(() => ApiKeyService.getInstance(), []);
  const customerService = useMemo(() => CustomerService.getInstance(), []);
  const locale = i18n.resolvedLanguage || i18n.language || "en-US";
  const browserTimezone = useMemo(() => getBrowserTimezone(), []);
  const translateRef = useRef(t);
  const createCustomerInputRef = useRef<HTMLInputElement>(null);
  const createNameInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    document.title = t("management.apiKeys.documentTitle");
    translateRef.current = t;
  }, [t]);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const apiKeysResponse = await apiKeyService.getAllApiKeys();

      if (apiKeysResponse.success) {
        setApiKeys(apiKeysResponse.data);
      } else {
        enqueueSnackbar(
          apiKeysResponse.message ||
            translateRef.current("management.apiKeys.errors.failedFetchApiKeys"),
          { variant: "error" },
        );
      }

      const customersResponse = await customerService.getAllCustomers();
      if (customersResponse.success && customersResponse.data) {
        setCustomers(customersResponse.data);
      } else {
        enqueueSnackbar(
          customersResponse.error ||
            translateRef.current("management.apiKeys.errors.failedFetchCustomers"),
          { variant: "error" },
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, [apiKeyService, customerService, enqueueSnackbar]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const customerById = useMemo(() => {
    return new Map(customers.map((customer) => [customer.id, customer]));
  }, [customers]);
  const { sortColumn, sortDirection, handleSort, sortedItems } = useTableSorting(
    apiKeys,
    (apiKey, column) =>
      ({
        customer: customerById.get(apiKey.customerId)?.name,
        key: apiKey.value,
        purpose: apiKey.name,
        role: getRoleDisplayName(apiKey.role),
        createdBy: apiKey.createdBy ? getAuditActorName(apiKey.createdBy) : undefined,
        created: apiKey.createdAt ? new Date(apiKey.createdAt).getTime() : null,
        lastUsed: apiKey.lastUsed ? new Date(apiKey.lastUsed).getTime() : null,
        status: apiKey.active,
      })[column],
    "customer" as
      "customer" | "key" | "purpose" | "role" | "createdBy" | "created" | "lastUsed" | "status",
    locale,
  );
  const [recordPage, setRecordPage] = useState(0);
  const [recordPageSize, setRecordPageSize] = useState(25);
  const currentRecordPage = Math.min(
    recordPage,
    Math.max(0, Math.ceil(sortedItems.length / recordPageSize) - 1),
  );
  const displayedRecords = sortedItems.slice(
    currentRecordPage * recordPageSize,
    (currentRecordPage + 1) * recordPageSize,
  );
  useEffect(() => {
    setRecordPage(0);
  }, [sortColumn, sortDirection]);
  const renderRecordPagination = () => (
    <LocalizedTablePagination
      component="div"
      count={sortedItems.length}
      page={currentRecordPage}
      rowsPerPage={recordPageSize}
      rowsPerPageOptions={[25, 50, 100]}
      onPageChange={(_, next) => setRecordPage(next)}
      onRowsPerPageChange={(event) => {
        setRecordPageSize(Number(event.target.value));
        setRecordPage(0);
      }}
    />
  );

  const handleCreate = async () => {
    setCreateDialogError(null);
    const nextFieldErrors = {
      customerId: !formData.customerId,
      name: !formData.name.trim(),
    };
    setCreateFieldErrors(nextFieldErrors);
    if (nextFieldErrors.customerId || nextFieldErrors.name) {
      (nextFieldErrors.customerId ? createCustomerInputRef : createNameInputRef).current?.focus();
      return;
    }

    setIsCreating(true);
    try {
      const response = await apiKeyService.createApiKey({
        customerId: Number(formData.customerId),
        name: formData.name.trim(),
        role: formData.role,
        validUntil: toDateOnlyIsoString(parseDateOnly(formData.validUntil)),
      });

      if (response.success && response.data) {
        setCreatedApiKey(response.data);
        closeCreateDialog();
        setFormData({
          customerId: "",
          name: "",
          role: UserRole.STORE_ADMIN,
          validUntil: "",
        });
        await loadData();
      } else {
        setCreateDialogError(response.message || t("management.apiKeys.errors.failedCreateApiKey"));
      }
    } catch (error) {
      setCreateDialogError(
        error instanceof Error ? error.message : t("management.apiKeys.errors.failedCreateApiKey"),
      );
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopyCreatedKey = async () => {
    if (!createdApiKey?.value) {
      return;
    }
    await navigator.clipboard.writeText(createdApiKey.value);
    enqueueSnackbar(t("management.apiKeys.messages.apiKeyCopied"), { variant: "success" });
  };

  const openEditDialog = (apiKey: ApiKey) => {
    setEditDialogError(null);
    setEditingApiKey(apiKey);
    setEditValidUntil(apiKey.validUntil ? apiKey.validUntil.slice(0, 10) : "");
    setEditActive(apiKey.active);
  };

  const handleSaveApiKey = async () => {
    if (!editingApiKey) {
      return;
    }

    setEditDialogError(null);
    setIsSavingApiKey(true);
    try {
      const response = await apiKeyService.updateApiKey({
        id: editingApiKey.id,
        active: editActive,
        validUntil: toDateOnlyIsoString(parseDateOnly(editValidUntil)),
      });
      if (response.success) {
        enqueueSnackbar(t("management.apiKeys.messages.apiKeyUpdated"), { variant: "success" });
        closeEditDialog();
        await loadData();
      } else {
        setEditDialogError(response.message || t("management.apiKeys.errors.failedUpdateApiKey"));
      }
    } catch (error) {
      setEditDialogError(
        error instanceof Error ? error.message : t("management.apiKeys.errors.failedUpdateApiKey"),
      );
    } finally {
      setIsSavingApiKey(false);
    }
  };

  const formatDateTime = (dateValue?: string | null) => {
    if (!dateValue) {
      return t("management.apiKeys.empty.never");
    }
    return formatStoreDateTimeWithTimezone(dateValue, browserTimezone, locale);
  };

  const openCreateDialog = () => {
    setCreateDialogError(null);
    setCreateFieldErrors({ customerId: false, name: false });
    setCreateDialogOpen(true);
  };

  const closeCreateDialog = () => {
    setCreateDialogOpen(false);
    setCreateDialogError(null);
    setCreateFieldErrors({ customerId: false, name: false });
  };

  const closeEditDialog = () => {
    setEditingApiKey(null);
    setEditDialogError(null);
  };

  return (
    <PageShell
      breadcrumbs="admin"
      title={t("management.apiKeys.title")}
      icon={<ApiKeysIcon />}
      titleAccessory={<LoadingRing loading={isLoading} />}
      headerActions={
        <Button
          variant="contained"
          color="primary"
          onClick={openCreateDialog}
          startIcon={<AddIcon />}
          disabled={isLoading}
        >
          {t("management.apiKeys.actions.generateApiKey")}
        </Button>
      }
    >
      <AdminTablePanel
        header={renderRecordPagination()}
        footer={renderRecordPagination()}
        mobileContent={
          <Box
            sx={{
              minWidth: 0,
              "& .MuiTypography-root": {
                maxWidth: "100%",
                whiteSpace: "normal",
                overflowWrap: "anywhere",
              },
            }}
          >
            <RecordSortControl
              value={sortColumn}
              direction={sortDirection}
              onSort={handleSort}
              options={[
                { value: "customer" as const, label: t("management.apiKeys.fields.customer") },
                { value: "key" as const, label: t("management.apiKeys.fields.key") },
                { value: "purpose" as const, label: t("management.apiKeys.fields.purpose") },
                { value: "role" as const, label: t("management.apiKeys.fields.role") },
                { value: "createdBy" as const, label: t("management.apiKeys.fields.createdBy") },
                { value: "created" as const, label: t("management.apiKeys.fields.created") },
                { value: "lastUsed" as const, label: t("management.apiKeys.fields.lastUsed") },
                { value: "status" as const, label: t("management.apiKeys.fields.status") },
              ]}
            />
            <RecordList
              items={displayedRecords}
              getItemId={(apiKey) => apiKey.id}
              loading={isLoading}
              resetKey={JSON.stringify([
                sortColumn,
                sortDirection,
                ...displayedRecords.map((apiKey) => apiKey.id),
              ])}
              renderTitle={(apiKey) => {
                return <>{apiKey.name}</>;
              }}
              renderSummary={(apiKey) => {
                return (
                  <RecordFields
                    fields={[
                      {
                        label: <>{t("management.apiKeys.fields.customer")}</>,
                        value: (
                          <>
                            <Typography variant="body2" fontWeight="medium">
                              {customerById.get(apiKey.customerId)?.name ||
                                t("management.apiKeys.fallback.customerWithId", {
                                  customerId: apiKey.customerId,
                                })}
                            </Typography>
                          </>
                        ),
                      },
                      {
                        label: <>{t("management.apiKeys.fields.role")}</>,
                        value: (
                          <>
                            <Chip
                              label={getRoleDisplayName(apiKey.role)}
                              size="small"
                              variant="outlined"
                            />
                          </>
                        ),
                      },
                    ]}
                  />
                );
              }}
              renderDetails={(apiKey) => {
                return (
                  <RecordFields
                    fields={[
                      {
                        label: <>{t("management.apiKeys.fields.key")}</>,
                        value: (
                          <>
                            <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                              {apiKey.value || "-"}
                            </Typography>
                          </>
                        ),
                      },
                      {
                        label: <>{t("management.apiKeys.fields.createdBy")}</>,
                        value: (
                          <>
                            <AuditActor value={apiKey.createdBy} fallback="-" />
                          </>
                        ),
                      },
                      {
                        label: <>{t("management.apiKeys.fields.created")}</>,
                        value: <>{formatDateTime(apiKey.createdAt)}</>,
                      },
                      {
                        label: <>{t("management.apiKeys.fields.lastUsed")}</>,
                        value: <>{formatDateTime(apiKey.lastUsed)}</>,
                      },
                      {
                        label: <>{t("management.apiKeys.fields.status")}</>,
                        value: (
                          <>
                            <Chip
                              label={
                                apiKey.active
                                  ? t("management.apiKeys.status.active")
                                  : t("management.apiKeys.status.inactive")
                              }
                              size="small"
                              color={apiKey.active ? "success" : "default"}
                              variant="outlined"
                            />
                          </>
                        ),
                      },
                    ]}
                  />
                );
              }}
              renderActions={(apiKey) => (
                <AdminTableActions
                  actions={[
                    {
                      icon: <EditIcon />,
                      hint: t("management.apiKeys.tooltips.editApiKey"),
                      ariaLabel: t("management.apiKeys.actions.editNamedApiKey", {
                        name: apiKey.name,
                      }),
                      action: () => openEditDialog(apiKey),
                    },
                  ]}
                />
              )}

              emptyState={
                <AdminEmptyState
                  icon={<ApiKeysIcon sx={{ fontSize: 40 }} />}
                  text={t("management.apiKeys.empty.noApiKeysFound")}
                />
              }
            />
          </Box>
        }
      >
        <TableHead>
          <TableRow>
            <SortableTableCell
              activeColumn={sortColumn}
              column="customer"
              direction={sortDirection}
              onSort={handleSort}
            >
              {t("management.apiKeys.fields.customer")}
            </SortableTableCell>
            <SortableTableCell
              activeColumn={sortColumn}
              column="key"
              direction={sortDirection}
              onSort={handleSort}
            >
              {t("management.apiKeys.fields.key")}
            </SortableTableCell>
            <SortableTableCell
              activeColumn={sortColumn}
              column="purpose"
              direction={sortDirection}
              onSort={handleSort}
            >
              {t("management.apiKeys.fields.purpose")}
            </SortableTableCell>
            <SortableTableCell
              activeColumn={sortColumn}
              column="role"
              direction={sortDirection}
              onSort={handleSort}
            >
              {t("management.apiKeys.fields.role")}
            </SortableTableCell>
            <SortableTableCell
              activeColumn={sortColumn}
              column="createdBy"
              direction={sortDirection}
              onSort={handleSort}
            >
              {t("management.apiKeys.fields.createdBy")}
            </SortableTableCell>
            <SortableTableCell
              activeColumn={sortColumn}
              column="created"
              direction={sortDirection}
              onSort={handleSort}
            >
              {t("management.apiKeys.fields.created")}
            </SortableTableCell>
            <SortableTableCell
              activeColumn={sortColumn}
              column="lastUsed"
              direction={sortDirection}
              onSort={handleSort}
            >
              {t("management.apiKeys.fields.lastUsed")}
            </SortableTableCell>
            <SortableTableCell
              activeColumn={sortColumn}
              column="status"
              direction={sortDirection}
              onSort={handleSort}
            >
              {t("management.apiKeys.fields.status")}
            </SortableTableCell>
            <TableCell align="right">{t("management.apiKeys.fields.actions")}</TableCell>
          </TableRow>
        </TableHead>
        <AdminTableBodyState
          items={displayedRecords}
          isLoading={isLoading}
          colSpan={9}
          loadingLabel={t("common.loading")}
          emptyState={
            <AdminEmptyState
              icon={<ApiKeysIcon sx={{ fontSize: 40 }} />}
              text={t("management.apiKeys.empty.noApiKeysFound")}
            />
          }
        >
          {(apiKey) => (
            <TableRow key={apiKey.id} hover>
              <TableCell>
                <Typography variant="body2" fontWeight="medium">
                  {customerById.get(apiKey.customerId)?.name ||
                    t("management.apiKeys.fallback.customerWithId", {
                      customerId: apiKey.customerId,
                    })}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                  {apiKey.value || "-"}
                </Typography>
              </TableCell>
              <TableCell>{apiKey.name}</TableCell>
              <TableCell>
                <Chip label={getRoleDisplayName(apiKey.role)} size="small" variant="outlined" />
              </TableCell>
              <TableCell>
                <AuditActor value={apiKey.createdBy} fallback="-" />
              </TableCell>
              <TableCell>{formatDateTime(apiKey.createdAt)}</TableCell>
              <TableCell>{formatDateTime(apiKey.lastUsed)}</TableCell>
              <TableCell>
                <Chip
                  label={
                    apiKey.active
                      ? t("management.apiKeys.status.active")
                      : t("management.apiKeys.status.inactive")
                  }
                  size="small"
                  color={apiKey.active ? "success" : "default"}
                  variant="outlined"
                />
              </TableCell>
              <TableCell align="right">
                <AdminTableActions
                  actions={[
                    {
                      icon: <EditIcon />,
                      hint: t("management.apiKeys.tooltips.editApiKey"),
                      ariaLabel: t("management.apiKeys.actions.editNamedApiKey", {
                        name: apiKey.name,
                      }),
                      action: () => openEditDialog(apiKey),
                    },
                  ]}
                />
              </TableCell>
            </TableRow>
          )}
        </AdminTableBodyState>
      </AdminTablePanel>

      <AdminDialog
        open={createDialogOpen}
        onClose={closeCreateDialog}
        maxWidth="sm"
        title={t("management.apiKeys.actions.generateApiKey")}
        icon={<ApiKeysIcon />}
        contentSx={{ display: "flex", flexDirection: "column", gap: 2 }}
        error={createDialogError}
        errorOperation="create"
        errorEntity={t("common.entities.apiKey")}
        cancelAction={{ label: t("common.cancel"), onClick: closeCreateDialog }}
        primaryAction={{
          label: isCreating
            ? t("management.apiKeys.actions.generating")
            : t("management.apiKeys.actions.generate"),
          onClick: () => void handleCreate(),
          loading: isCreating,
        }}
      >
        <MuiAutocompleteMultiSelect
          label={t("management.apiKeys.fields.customer")}
          options={customers}
          value={customers.filter((customer) => customer.id.toString() === formData.customerId)}
          onChange={(next) => {
            const customer = next[0];
            setFormData((current) => ({ ...current, customerId: customer?.id.toString() ?? "" }));
            if (customer) {
              setCreateFieldErrors((current) => ({ ...current, customerId: false }));
            }
          }}
          getOptionLabel={(customer) => customer.name}
          getOptionKey={(customer) => customer.id}
          required
          error={createFieldErrors.customerId}
          helperText={createFieldErrors.customerId ? t("common.required") : undefined}
          inputRef={createCustomerInputRef}
        />
        <TextField
          label={t("management.apiKeys.fields.purpose")}
          value={formData.name}
          inputRef={createNameInputRef}
          onChange={(event) => {
            setFormData((current) => ({ ...current, name: event.target.value }));
            if (event.target.value.trim()) {
              setCreateFieldErrors((current) => ({ ...current, name: false }));
            }
          }}
          error={createFieldErrors.name}
          helperText={createFieldErrors.name ? t("common.required") : undefined}
          fullWidth
        />
        <FormControl fullWidth>
          <InputLabel id="api-key-role-label">{t("management.apiKeys.fields.role")}</InputLabel>
          <Select
            labelId="api-key-role-label"
            label={t("management.apiKeys.fields.role")}
            value={formData.role}
            onChange={(event) =>
              setFormData((current) => ({
                ...current,
                role: event.target.value,
              }))
            }
          >
            <MenuItem value={UserRole.STORE_ADMIN}>
              {getRoleDisplayName(UserRole.STORE_ADMIN)}
            </MenuItem>
            <MenuItem value={UserRole.CUSTOMER_ADMIN}>
              {getRoleDisplayName(UserRole.CUSTOMER_ADMIN)}
            </MenuItem>
            <MenuItem value={UserRole.ASSOCIATE}>{getRoleDisplayName(UserRole.ASSOCIATE)}</MenuItem>
          </Select>
        </FormControl>
        <AdminDatePicker
          label={t("management.apiKeys.fields.validUntil")}
          value={parseDateOnly(formData.validUntil)}
          onChange={(date) =>
            setFormData((current) => ({
              ...current,
              validUntil: date ? toDateOnlyKey(date) : "",
            }))
          }
        />
      </AdminDialog>

      <AdminDialog
        open={Boolean(createdApiKey)}
        onClose={() => setCreatedApiKey(null)}
        maxWidth="sm"
        title={t("management.apiKeys.dialogs.generatedTitle")}
        icon={<ApiKeysIcon />}
        contentSx={{ display: "flex", flexDirection: "column", gap: 2 }}
        secondaryAction={{
          label: t("management.apiKeys.actions.copy"),
          icon: <CopyIcon />,
          onClick: () => void handleCopyCreatedKey(),
        }}
        primaryAction={{ label: t("common.done"), onClick: () => setCreatedApiKey(null) }}
      >
        <Alert severity="warning">{t("management.apiKeys.warnings.keyShownOnce")}</Alert>
        <TextField
          label={t("management.apiKeys.fields.apiKey")}
          value={createdApiKey?.value || ""}
          fullWidth
          multiline
          minRows={2}
          slotProps={{ input: { readOnly: true } }}
        />
      </AdminDialog>

      <AdminDialog
        open={Boolean(editingApiKey)}
        onClose={closeEditDialog}
        maxWidth="sm"
        title={t("management.apiKeys.dialogs.detailsTitle")}
        icon={<ApiKeysIcon />}
        contentSx={{ display: "flex", flexDirection: "column", gap: 2 }}
        error={editDialogError}
        errorOperation="update"
        errorEntity={t("common.entities.apiKey")}
        cancelAction={{
          label: t("management.apiKeys.actions.close"),
          onClick: closeEditDialog,
          disabled: isSavingApiKey,
        }}
        primaryAction={{
          label: isSavingApiKey
            ? t("management.apiKeys.actions.saving")
            : t("management.apiKeys.actions.save"),
          onClick: () => void handleSaveApiKey(),
          loading: isSavingApiKey,
        }}
      >
        <DetailRow
          label={t("management.apiKeys.fields.customer")}
          value={
            editingApiKey
              ? customerById.get(editingApiKey.customerId)?.name ||
                t("management.apiKeys.fallback.customerWithId", {
                  customerId: editingApiKey.customerId,
                })
              : ""
          }
        />
        <DetailRow
          label={t("management.apiKeys.fields.key")}
          value={editingApiKey?.value || "-"}
          monospace
        />
        <DetailRow
          label={t("management.apiKeys.fields.purpose")}
          value={editingApiKey?.name || ""}
        />
        <DetailRow
          label={t("management.apiKeys.fields.role")}
          value={editingApiKey?.role ? getRoleDisplayName(editingApiKey.role) : ""}
        />
        <DetailRow
          label={t("management.apiKeys.fields.createdBy")}
          value={<AuditActor value={editingApiKey?.createdBy} fallback="-" />}
        />
        <DetailRow
          label={t("management.apiKeys.fields.created")}
          value={formatDateTime(editingApiKey?.createdAt)}
        />
        <DetailRow
          label={t("management.apiKeys.fields.lastUsed")}
          value={formatDateTime(editingApiKey?.lastUsed)}
        />
        <FormControlLabel
          control={
            <Switch
              checked={editActive}
              onChange={(event) => setEditActive(event.target.checked)}
            />
          }
          label={
            editActive
              ? t("management.apiKeys.status.active")
              : t("management.apiKeys.status.inactive")
          }
        />
        <AdminDatePicker
          label={t("management.apiKeys.fields.validUntil")}
          value={parseDateOnly(editValidUntil)}
          onChange={(date) => setEditValidUntil(date ? toDateOnlyKey(date) : "")}
        />
      </AdminDialog>
    </PageShell>
  );
};

export default ApiKeys;
